import React, { useState } from 'react';
import { FaCalendarAlt, FaTimes, FaDoorOpen, FaFileAlt, FaExternalLinkAlt } from 'react-icons/fa';
import StagePlayer from '../UnityGame/StagePlayer';

interface Room {
  room_id: string;
  room_name: string;
  event_id: string;
  chat_room_arn: string;
  stage_arn: string;
  stage_playback_url?: string;
}

interface AgendaItem {
  id: string;
  event_id: string;
  title: string;
  subtitle: string;
  time_utc: string;
  status: string;
  room_id?: string;
  duration_minutes?: number;
}

// Mock data for sandbox
const MOCK_ROOMS: Room[] = [
  {
    room_id: 'intersect-main',
    room_name: 'Main Stage',
    event_id: '22222',
    chat_room_arn: 'arn:aws:ivschat:us-west-2:390409532441:room/A02aYaQTK0b2',
    stage_arn: 'arn:aws:ivs:us-west-2:390409532441:stage/uMXabyK915IH',
    stage_playback_url: 'https://3d50b24ba88e.us-west-2.playback.live-video.net/api/video/v1/us-west-2.049054135175.channel.6k(+more characters).m3u8',
  },
  {
    room_id: 'breakout-1',
    room_name: 'Breakout Room 1',
    event_id: '22222',
    chat_room_arn: 'arn:aws:ivschat:us-west-2:390409532441:room/Dnm0Rz9xUz4C',
    stage_arn: 'arn:aws:ivs:us-west-2:390409532441:stage/PjvitesOthd1',
    stage_playback_url: 'https://3d50b24ba88e.us-west-2.playback.live-video.net/api/video/v1/us-west-2.049054135175.channel.6ktest.m3u8',
  },
  {
    room_id: 'breakout-2',
    room_name: 'Breakout Room 2',
    event_id: '22222',
    chat_room_arn: 'arn:aws:ivschat:us-west-2:390409532441:room/Test123',
    stage_arn: 'arn:aws:ivs:us-west-2:390409532441:stage/Test456',
    stage_playback_url: 'https://3d50b24ba88e.us-west-2.playback.live-video.net/api/video/v1/us-west-2.049054135175.channel.test2.m3u8',
  },
  {
    room_id: 'networking-lounge',
    room_name: 'Networking Lounge',
    event_id: '22222',
    chat_room_arn: '',
    stage_arn: '',
    // No stage_playback_url - this room won't be shown in sandbox
  },
  {
    room_id: 'call-room-enter-1',
    room_name: 'Call Room 1',
    event_id: '22222',
    chat_room_arn: '',
    stage_arn: '',
  },
];

const MOCK_AGENDA: AgendaItem[] = [
  {
    id: '1',
    event_id: '22222',
    title: 'Opening Keynote',
    subtitle: 'Welcome and introduction to the Annual Members Meeting',
    time_utc: new Date(Date.now() - 30 * 60 * 1000).toISOString(), // 30 minutes ago - ongoing
    status: 'ongoing',
    room_id: 'intersect-main',
    duration_minutes: 60,
  },
  {
    id: '2',
    event_id: '22222',
    title: 'Technical Workshop: Smart Contracts',
    subtitle: 'Deep dive into Cardano smart contract development',
    time_utc: new Date(Date.now() + 30 * 60 * 1000).toISOString(), // 30 minutes from now
    status: 'upcoming',
    room_id: 'breakout-1',
    duration_minutes: 90,
  },
  {
    id: '3',
    event_id: '22222',
    title: 'Governance Discussion',
    subtitle: 'Community governance and voting mechanisms',
    time_utc: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(), // 2 hours from now
    status: 'upcoming',
    room_id: 'breakout-2',
    duration_minutes: 60,
  },
  {
    id: '4',
    event_id: '22222',
    title: 'Networking Session',
    subtitle: 'Open networking and Q&A',
    time_utc: new Date(Date.now() + 3 * 60 * 60 * 1000).toISOString(), // 3 hours from now
    status: 'upcoming',
    room_id: 'networking-lounge',
    duration_minutes: 45,
  },
  {
    id: '5',
    event_id: '22222',
    title: 'Closing Remarks',
    subtitle: 'Wrap-up and next steps',
    time_utc: new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString(), // 4 hours from now
    status: 'upcoming',
    room_id: 'intersect-main',
    duration_minutes: 30,
  },
];

const callRoomLinks: { [key: string]: string } = {
  'call-room-enter-1': 'https://meet.google.com/wdg-nbqh-vxu',
};

const IntersectEventDesktopFallbackSandbox: React.FC = () => {
  const [rooms] = useState<Room[]>(MOCK_ROOMS);
  const [currentRoom, setCurrentRoom] = useState<Room>(MOCK_ROOMS[0]);
  const [agendaItems] = useState<AgendaItem[]>(MOCK_AGENDA);
  const [isStageMuted, setIsStageMuted] = useState(true);
  const [showAgendaModal, setShowAgendaModal] = useState(false);

  const handleRoomSelect = (room: Room) => {
    if (callRoomLinks[room.room_id]) {
      console.log('[Sandbox] Call room selected, would open:', callRoomLinks[room.room_id]);
      alert(`Would open Google Meet: ${callRoomLinks[room.room_id]}`);
      return;
    }

    console.log('[Sandbox] Switching to room:', room.room_name);
    setCurrentRoom(room);
  };

  const handleToggleStageMute = () => {
    setIsStageMuted(!isStageMuted);
  };

  const getActualStatus = (item: AgendaItem): 'upcoming' | 'ongoing' | 'completed' => {
    const now = new Date();
    const itemStartTime = new Date(item.time_utc);
    const durationMs = (item.duration_minutes || 60) * 60 * 1000;
    const itemEndTime = new Date(itemStartTime.getTime() + durationMs);

    if (now < itemStartTime) {
      return 'upcoming';
    } else if (now >= itemStartTime && now <= itemEndTime) {
      return 'ongoing';
    } else {
      return 'completed';
    }
  };

  const formatTime = (utcTimeString: string) => {
    const date = new Date(utcTimeString);
    const hours = date.getUTCHours().toString().padStart(2, '0');
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes} UTC`;
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'ongoing':
        return 'Live Now';
      case 'upcoming':
        return 'Upcoming';
      default:
        return status;
    }
  };

  const handleAgendaItemClick = (item: AgendaItem) => {
    if (!item.room_id) return;
    
    const actualStatus = getActualStatus(item);
    if (actualStatus !== 'ongoing') return;
    if (item.room_id === currentRoom?.room_id) return;
    
    const room = rooms.find(r => r.room_id === item.room_id);
    if (!room) {
      console.warn('[Sandbox Agenda] Room not found:', item.room_id);
      return;
    }
    
    handleRoomSelect(room);
    setShowAgendaModal(false);
  };

  const filteredAgendaItems = agendaItems
    .filter((item) => {
      const actualStatus = getActualStatus(item);
      return actualStatus !== 'completed';
    })
    .sort((a, b) => new Date(a.time_utc).getTime() - new Date(b.time_utc).getTime());

  const happeningNowItem = filteredAgendaItems.find(item => {
    const actualStatus = getActualStatus(item);
    return actualStatus === 'ongoing';
  });

  const displayRooms = rooms.filter((room) => {
    const isCallRoom = !!callRoomLinks[room.room_id];
    return isCallRoom || room.stage_playback_url;
  });

  return (
    <div className="min-h-screen bg-black text-white flex">
      {/* Left Sidebar - Rooms */}
      <div className="w-80 bg-[#1a1a1a] border-r border-[#333] flex flex-col">
        <div className="p-4 border-b border-[#333]">
          <h2 className="text-xl font-bold">Intersect Virtual Hub</h2>
          <p className="text-sm text-gray-400 mt-1">Select a Room</p>
          <p className="text-xs text-yellow-500 mt-2">🧪 Sandbox Mode</p>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {displayRooms.map((room) => {
            const isCallRoom = !!callRoomLinks[room.room_id];
            const isCurrentRoom = currentRoom?.room_id === room.room_id;
            
            return (
              <button
                key={room.room_id}
                onClick={() => handleRoomSelect(room)}
                className={`w-full text-left p-4 rounded-lg transition-all duration-200 ${
                  isCurrentRoom
                    ? 'bg-purple-600 text-white shadow-lg'
                    : 'bg-[#2a2a2a] text-white hover:bg-[#333]'
                }`}
              >
                <div className="font-semibold text-lg">{room.room_name}</div>
                <div className="text-xs text-gray-300 mt-1">{room.room_id}</div>
                {isCallRoom && (
                  <div className="text-xs text-blue-400 mt-2 flex items-center gap-1">
                    🔗 Opens Google Meet
                  </div>
                )}
                {!isCallRoom && room.stage_playback_url && (
                  <div className="text-xs text-green-400 mt-2 flex items-center gap-1">
                    ● Live Stream Available
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* WebGPU Notice */}
        <div className="p-4 border-t border-[#333] bg-orange-900/20">
          <p className="text-xs text-orange-400">
            ⚠️ WebGPU not supported on this browser. Showing stream-only view.
          </p>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <div className="bg-[#1a1a1a] border-b border-[#333] p-4 flex items-center justify-between">
          <div className="flex-1">
            <h1 className="text-2xl font-bold">
              {currentRoom ? currentRoom.room_name : 'No Room Selected'}
            </h1>
            {currentRoom && (
              <p className="text-sm text-gray-400 mt-1">{currentRoom.room_id}</p>
            )}
          </div>
          
          <button
            onClick={() => setShowAgendaModal(true)}
            className="px-4 py-2 bg-purple-600 bg-opacity-80 text-white rounded-lg hover:bg-opacity-100 transition-all duration-200 font-semibold flex items-center gap-2"
          >
            <FaCalendarAlt />
            Agenda
          </button>
        </div>

        {/* Stream Player */}
        <div className="flex-1 flex items-center justify-center bg-black">
          {currentRoom?.stage_playback_url ? (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900/20 to-blue-900/20">
              <div className="text-center p-8">
                <div className="text-6xl mb-4">🎥</div>
                <p className="text-gray-300 text-xl mb-2">Live Stream Player</p>
                <p className="text-gray-500 text-sm">Sandbox mode - stream player would appear here</p>
                <p className="text-gray-600 text-xs mt-4 max-w-md">
                  URL: {currentRoom.stage_playback_url.substring(0, 50)}...
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center p-8">
              <p className="text-gray-400 text-xl mb-2">No stream available for this room</p>
              <p className="text-gray-500">Select a different room from the sidebar</p>
            </div>
          )}
        </div>

        {/* Happening Now Banner */}
        {happeningNowItem && (
          <div className="bg-[#1a1a1a] border-t border-[#333] p-4">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-sm font-semibold text-purple-400 mb-2">HAPPENING NOW</h3>
              <div 
                onClick={() => handleAgendaItemClick(happeningNowItem)}
                className={`bg-[#2a2a2a] rounded-lg p-4 flex items-center justify-between ${
                  happeningNowItem.room_id && happeningNowItem.room_id !== currentRoom?.room_id 
                    ? 'cursor-pointer hover:bg-[#333] border border-purple-600' 
                    : happeningNowItem.room_id === currentRoom?.room_id
                      ? 'border border-green-500'
                      : ''
                }`}
              >
                <div>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <div className="text-sm text-gray-400 font-mono">
                      {formatTime(happeningNowItem.time_utc)}
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-green-500 text-white animate-pulse">
                      Live Now
                    </span>
                    {happeningNowItem.room_id && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold flex items-center gap-1 ${
                        happeningNowItem.room_id === currentRoom?.room_id 
                          ? 'bg-green-500 text-white'
                          : 'bg-purple-600 text-white'
                      }`}>
                        <FaDoorOpen size={10} />
                        {happeningNowItem.room_id === currentRoom?.room_id ? 'Current Room' : 'Click to Join'}
                      </span>
                    )}
                  </div>
                  <h4 className="text-lg font-bold text-white">{happeningNowItem.title}</h4>
                  {happeningNowItem.subtitle && (
                    <p className="text-sm text-gray-300 mt-1">{happeningNowItem.subtitle}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Agenda Modal */}
      {showAgendaModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10001]"
          onClick={() => setShowAgendaModal(false)}
        >
          <div 
            className="bg-[#1a1a1a] rounded-lg shadow-2xl border border-[#333] w-[90%] max-w-3xl max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-[#333]">
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <FaCalendarAlt className="text-purple-500" />
                AMM Agenda
              </h2>
              <button
                onClick={() => setShowAgendaModal(false)}
                className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-[#2a2a2a] rounded"
              >
                <FaTimes size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {/* Document Links */}
                <div className="space-y-3 pb-4 mb-4 border-b border-[#444]">
                  <a
                    href="https://docs.intersectmbo.org/intersect-membership/annual-member-meetings-amm/2025-annual-members-meeting/intersect-development-update-2025-report"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 rounded-lg bg-blue-900/20 border border-blue-600/30 hover:bg-blue-900/30 hover:border-blue-500/50 transition-all duration-200 group"
                  >
                    <FaFileAlt className="text-blue-400 flex-shrink-0" size={20} />
                    <div className="flex-1">
                      <div className="text-white font-semibold group-hover:text-blue-300 transition-colors">
                        Read the 2025 Report
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        Intersect Development Update - 2025 Report
                      </div>
                    </div>
                    <FaExternalLinkAlt className="text-blue-400 flex-shrink-0" size={12} />
                  </a>
                  
                  <a
                    href="https://docs.intersectmbo.org/intersect-membership/annual-member-meetings-amm/2025-annual-members-meeting/intersect-purpose-3-years-mission-and-vision-and-2026-goals"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 rounded-lg bg-purple-900/20 border border-purple-600/30 hover:bg-purple-900/30 hover:border-purple-500/50 transition-all duration-200 group"
                  >
                    <FaFileAlt className="text-purple-400 flex-shrink-0" size={20} />
                    <div className="flex-1">
                      <div className="text-white font-semibold group-hover:text-purple-300 transition-colors">
                        Read the 2026 Proposal
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        Intersect Purpose, 3 years mission and vision & 2026 goals
                      </div>
                    </div>
                    <FaExternalLinkAlt className="text-purple-400 flex-shrink-0" size={12} />
                  </a>
                </div>

                {/* Agenda Items */}
                {filteredAgendaItems.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    No agenda items available
                  </div>
                ) : (
                  filteredAgendaItems.map((item) => {
                    const actualStatus = getActualStatus(item);
                    const hasRoom = !!item.room_id;
                    const isCurrentRoom = item.room_id === currentRoom?.room_id;
                    const isOngoing = actualStatus === 'ongoing';
                    const isClickable = hasRoom && isOngoing && !isCurrentRoom;
                    
                    return (
                      <div
                        key={item.id}
                        onClick={() => isClickable && handleAgendaItemClick(item)}
                        className={`p-4 rounded-lg border transition-all duration-200 ${
                          actualStatus === 'ongoing' 
                            ? 'bg-green-900/30 border-green-500/50 text-green-300'
                            : 'bg-blue-900/30 border-blue-500/50 text-blue-300'
                        } ${
                          isClickable ? 'cursor-pointer hover:bg-opacity-50 hover:border-purple-500' : ''
                        } ${isCurrentRoom ? 'ring-2 ring-green-500' : ''}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                              <span className="text-sm font-mono font-semibold">
                                {formatTime(item.time_utc)}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                                actualStatus === 'ongoing' 
                                  ? 'bg-green-500 text-white animate-pulse' 
                                  : 'bg-blue-500 text-white'
                              }`}>
                                {getStatusLabel(actualStatus)}
                              </span>
                              {hasRoom && (
                                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold flex items-center gap-1 ${
                                  isCurrentRoom 
                                    ? 'bg-green-500 text-white'
                                    : isOngoing
                                      ? 'bg-purple-600 text-white'
                                      : 'bg-gray-600 text-gray-400'
                                }`}>
                                  <FaDoorOpen size={10} />
                                  {isCurrentRoom ? 'Current Room' : isOngoing ? 'Click to Join' : 'Not Available'}
                                </span>
                              )}
                            </div>
                            <h3 className="text-lg font-bold mb-1">
                              {item.title}
                            </h3>
                            {item.subtitle && (
                              <p className="text-sm text-gray-400">
                                {item.subtitle}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-[#333] text-center">
              <p className="text-xs text-gray-500">
                All times displayed in UTC
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntersectEventDesktopFallbackSandbox;

